import { useState, useEffect, useRef } from "react";
import seriesStore from "../stores/seriesStore.js";
import statusStore from "../stores/statusStore.js";
import seasonStore from "../stores/seasonStore.js"
import Header from "../components/Header.js";
import Footer from "../components/Footer.js";

const ROWS_PER_PAGE = 130;

export default function GetStartedPage() {
    const serStore = seriesStore();
    const staStore = statusStore();
    const seaStore = seasonStore();

    const [selectedShow, setSelectedShow] = useState(null);
    const [selectedSeriesList, setSelectedSeriesList] = useState(null);
    const [activeHeader, setActiveHeader] = useState(false);

    const [genreFilter, setGenreFilter] = useState("");
    const [decadeFilter, setDecadeFilter] = useState("");
    const [networkFilter, setNetworkFilter] = useState("");

    const [genreOpen, setGenreOpen] = useState(false);
    const [decadeOpen, setDecadeOpen] = useState(false);
    const [networkOpen, setNetworkOpen] = useState(false);

    const genreRef = useRef(null);
    const decadeRef = useRef(null);
    const networkRef = useRef(null);

    // Runs the function on load
    useEffect(() => {
        serStore.fetchSeries();
        staStore.fetchStatus();
        seaStore.fetchSeason();
    }, []);

    useEffect(() => {
        if (!serStore.series || !staStore.status) return;

        const initialStatuses = {};
        staStore.status.forEach((s) => {
            if (s.userStatus) {
                initialStatuses[s.seriesId] = s.userStatus;
            }
        });

        setSelectedStatuses(initialStatuses);
    }, [serStore.series, staStore.status]);

    // Close filter dropdowns if clicked outside
    useEffect(() => {
        const handleClickOutside = (event) => {
        if (genreRef.current && !genreRef.current.contains(event.target)) {
            setGenreOpen(false);
        }
        if (decadeRef.current && !decadeRef.current.contains(event.target)) {
            setDecadeOpen(false);
        }
        if (networkRef.current && !networkRef.current.contains(event.target)) {
            setNetworkOpen(false);
        }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => {
        document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    // Build dropdown options from data
    const seriesList = serStore.series || [];
    const allGenres = [
        ...new Set(
            (seriesList || []).flatMap(s => s.genres || [])
        )
        ].sort();

    const allNetworks = [...new Set(seriesList.map(s => s.network).filter(Boolean))];

    const allDecades = [...new Set(
        seriesList
            .map(s => s.year ? Math.floor(s.year / 10) * 10 : null)
            .filter(Boolean)
        )].sort();

    // Filters
    const filteredSeries = seriesList.filter((s) => {
        const matchesGenre =
            !genreFilter || (s.genres || []).includes(genreFilter);

        const matchesNetwork =
            !networkFilter || s.network === networkFilter;

        const matchesDecade =
            !decadeFilter ||
            (s.year && Math.floor(s.year / 10) * 10 === Number(decadeFilter));

        return matchesGenre && matchesNetwork && matchesDecade;
    });

    // Pagination
    const [currentPage, setCurrentPage] = useState(1);
    const totalPages = Math.ceil(filteredSeries.length / ROWS_PER_PAGE);
    const startIndex = (currentPage - 1) * ROWS_PER_PAGE;
    const endIndex = startIndex + ROWS_PER_PAGE;
    const currentSeries = filteredSeries.slice(startIndex, endIndex);

    // Selected state for the buttons
    const [selectedStatuses, setSelectedStatuses] = useState({});

    const handleStatusClick = (seriesId, status, airingStatus) => {
        setSelectedStatuses((prev) => ({
            ...prev,
            [seriesId]: status
        }));

        staStore.updateStatus(seriesId, status, airingStatus);
    };

     if (!serStore.series || serStore.series.length === 0) {
        return (
        <div className="getStartedPage">
            <p>Loading...</p>
        </div>
        );
    }

    return (
        <div>
            <div className="app-wrapper">
                <header class="header">
                    <Header 
                        selectedShow={selectedShow}
                        setSelectedShow={setSelectedShow}
                        selectedSeriesList={selectedSeriesList}
                        setSelectedSeriesList={setSelectedSeriesList}
                        activeHeader={activeHeader}
                        setActiveHeader={setActiveHeader}
                    />
                </header>
                <div class="getStartedPage">
                <h1>Get Started</h1>
                    <div className="filters">
                        {/* Genre Dropdown */}
                        <div className="dropdown" ref={genreRef}>
                            <button
                            type="button"
                            onClick={() => setGenreOpen(!genreOpen)}
                            className={`getStartedDropdown ${genreOpen ? "open" : ""}`}
                            >
                            {genreFilter || "All Genres"}
                            <span className="dropdown-arrow">&#9662;</span>
                            </button>
                            {genreOpen && (
                            <ul className="getStartedDropdownMenu">
                                <li
                                className={`getstarted-dropdown-item ${genreFilter === "" ? "selected" : ""}`}
                                onClick={() => { setGenreFilter(""); setCurrentPage(1); setGenreOpen(false); }}
                                >
                                All Genres
                                </li>
                                {allGenres.sort((a, b) => a.localeCompare(b)).map((g) => (
                                <li
                                    key={g}
                                    className={`getstarted-dropdown-item ${genreFilter === g ? "selected" : ""}`}
                                    onClick={() => { setGenreFilter(g); setCurrentPage(1); setGenreOpen(false); }}
                                >
                                    {g}
                                </li>
                                ))}
                            </ul>
                            )}
                        </div>

                        {/* Decade Dropdown */}
                        <div className="dropdown" ref={decadeRef}>
                            <button
                            type="button"
                            onClick={() => setDecadeOpen(!decadeOpen)}
                            className={`getStartedDropdown ${decadeOpen ? "open" : ""}`}
                            >
                            {decadeFilter ? `${decadeFilter}s` : "All Decades"}
                            <span className="dropdown-arrow">&#9662;</span>
                            </button>
                            {decadeOpen && (
                            <ul className="getStartedDropdownMenu">
                                <li
                                className={`getstarted-dropdown-item ${decadeFilter === "" ? "selected" : ""}`}
                                onClick={() => { setDecadeFilter(""); setCurrentPage(1); setDecadeOpen(false); }}
                                >
                                All Decades
                                </li>
                                {allDecades.map((d) => (
                                <li
                                    key={d}
                                    className={`getstarted-dropdown-item ${decadeFilter === d ? "selected" : ""}`}
                                    onClick={() => { setDecadeFilter(d); setCurrentPage(1); setDecadeOpen(false); }}
                                >
                                    {d}s
                                </li>
                                ))}
                            </ul>
                            )}
                        </div>

                        {/* Network Dropdown */}
                        <div className="dropdown" ref={networkRef}>
                            <button
                            type="button"
                            onClick={() => setNetworkOpen(!networkOpen)}
                            className={`getStartedDropdown ${networkOpen ? "open" : ""}`}
                            >
                            {networkFilter || "All Networks"}
                            <span className="dropdown-arrow">&#9662;</span>
                            </button>
                            {networkOpen && (
                            <ul className="getStartedDropdownMenu">
                                <li
                                className={`getstarted-dropdown-item ${networkFilter === "" ? "selected" : ""}`}
                                onClick={() => { setNetworkFilter(""); setCurrentPage(1); setNetworkOpen(false); }}
                                >
                                All Networks
                                </li>
                                {allNetworks.sort((a, b) => a.localeCompare(b)).map((n) => (
                                <li
                                    key={n}
                                    className={`getstarted-dropdown-item ${networkFilter === n ? "selected" : ""}`}
                                    onClick={() => { setNetworkFilter(n); setCurrentPage(1); setNetworkOpen(false); }}
                                >
                                    {n}
                                </li>
                                ))}
                            </ul>
                            )}
                        </div>
                    </div>

                    <table className="getStartedTable">
                        <thead>
                            
                        </thead>

                        <tbody>
                        {currentSeries.map((s) => (
                            <tr key={s._id}>
                                <td className="columnShowName">{s.title}</td>

                                <td className="columnNotInterested">
                                    <button className={selectedStatuses[s._id] === "Not Interested" ? "selected notInterested" : ""}
                                    onClick={() => handleStatusClick(s._id, "Not Interested", s.airingStatus)}>Not Interested</button>
                                </td>

                                <td className="columnStarted">
                                    <button className={selectedStatuses[s._id] === "Started" ? "selected started" : ""}
                                    onClick={() => handleStatusClick(s._id, "Started", s.airingStatus)}>Started</button>
                                </td>

                                <td className="columnToWatch">
                                    <button className={selectedStatuses[s._id] === "To Watch" ? "selected toWatch" : ""}
                                    onClick={() => handleStatusClick(s._id, "To Watch", s.airingStatus)}>To Watch</button>
                                </td>

                                <td className="columnStopped">
                                    <button className={selectedStatuses[s._id] === "Stopped" ? "selected stopped" : ""}
                                    onClick={() => handleStatusClick(s._id, "Stopped", s.airingStatus)}>Stopped</button>
                                </td>

                                <td className="columnFinished">
                                    <button className={selectedStatuses[s._id] === "Finished" ? "selected finished" : ""}
                                    onClick={() => handleStatusClick(s._id, "Finished", s.airingStatus)}>Finished</button>
                                </td>
                            </tr>
                        ))}
                        </tbody>
                    </table>

                    {/* Pagination Controls */}
                    <div className="pagination">
                        <button
                        onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
                        disabled={currentPage === 1}
                        >
                        Previous
                        </button>

                        <span>
                        Page {currentPage} of {totalPages}
                        </span>

                        <button
                        onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
                        disabled={currentPage === totalPages}
                        >
                        Next
                        </button>

                    </div>
                </div>
                <div className="mobileGetStartedPage">
                    <h1>Get Started</h1>
                    <p className="hint">Unfortunately this page is only accessible on desktop.</p>
                </div>
            </div>
            <div class="footer"><Footer/></div>
      </div>
      )
}